import React, { useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Button,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../../ThemeContext';
import { Link } from 'react-router-dom';

// Icons
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import MovieFilterRoundedIcon from '@mui/icons-material/MovieFilterRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import HdRoundedIcon from '@mui/icons-material/HdRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded';
import LiveTvRoundedIcon from '@mui/icons-material/LiveTvRounded';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import GradeRoundedIcon from '@mui/icons-material/GradeRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import OndemandVideoRoundedIcon from '@mui/icons-material/OndemandVideoRounded';

// Images & Assets
import moviesPoster from '../../../assets/movies.png';

// Curated 4K Cinema Poster Showcase
const CINEMA_SHOWCASE = [
  {
    id: 1,
    title: 'Mad Max: Fury Road',
    category: 'Cinematic Release',
    quality: '4K HDR • 60 FPS',
    rating: '9.4',
    badge: 'LIVE 4K',
    audio: 'DOLBY ATMOS',
    views: '84.2K',
    poster: 'https://images.unsplash.com/photo-1536440136628-849c177e76a1?q=80&w=800&auto=format&fit=crop',
    accent: '#f59e0b',
  },
  {
    id: 2,
    title: 'Spectre 007',
    category: 'Studio Premiere',
    quality: 'IMAX Enhanced',
    rating: '9.1',
    badge: 'TRENDING #1',
    audio: '7.1 SURROUND',
    views: '124K',
    poster: 'https://images.unsplash.com/photo-1485846234645-a62644f84728?q=80&w=800&auto=format&fit=crop',
    accent: '#07a4f5',
  },
  {
    id: 3,
    title: 'The Matrix Resurgence',
    category: 'Indie Feature',
    quality: 'Dolby Vision',
    rating: '9.3',
    badge: 'DIRECT-TO-FAN',
    audio: 'SPATIAL PRO',
    views: '98K',
    poster: 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?q=80&w=800&auto=format&fit=crop',
    accent: '#10b981',
  },
  {
    id: 4,
    title: 'Scarface Re-Mastered',
    category: 'Classic Gold Vault',
    quality: '4K DCI Restoration',
    rating: '9.6',
    badge: 'STUDIO MASTER',
    audio: 'UNCOMPRESSED PCM',
    views: '150K',
    poster: 'https://images.unsplash.com/photo-1509281373149-e957c6296406?q=80&w=800&auto=format&fit=crop',
    accent: '#ec4899',
  },
];

const STREAMING_CAPABILITIES = [
  {
    icon: <BoltRoundedIcon sx={{ fontSize: 26, color: '#f59e0b' }} />,
    title: 'Zero-Buffer Global CDN',
    desc: 'Powered by 184 worldwide edge delivery nodes for instant playback starting in under 180 milliseconds.',
    badge: '< 18ms Latency',
    color: '#f59e0b',
  },
  {
    icon: <HdRoundedIcon sx={{ fontSize: 26, color: '#07a4f5' }} />,
    title: 'Pristine 4K HDR & 60 FPS',
    desc: 'Lossless studio dailies and commercial features rendered with 10-bit color depth and Dolby Vision grading.',
    badge: '4K Ultra DCI',
    color: '#07a4f5',
  },
  {
    icon: <VolumeUpRoundedIcon sx={{ fontSize: 26, color: '#8b5cf6' }} />,
    title: 'Dolby Atmos Spatial Sound',
    desc: 'Cinema-grade 360-degree spatial audio stream with binaural headphone rendering and 7.1.4 sound field support.',
    badge: 'Dolby Spatial',
    color: '#8b5cf6',
  },
  {
    icon: <SecurityRoundedIcon sx={{ fontSize: 26, color: '#10b981' }} />,
    title: '256-Bit Studio DRM Protection',
    desc: 'Multi-DRM protection (Widevine L1, Apple FairPlay, PlayReady) shielding releases from unauthorized duplication.',
    badge: 'Studio Grade DRM',
    color: '#10b981',
  },
  {
    icon: <OndemandVideoRoundedIcon sx={{ fontSize: 26, color: '#ec4899' }} />,
    title: 'Direct-to-Fan Box Office',
    desc: 'Independent filmmakers sell premiere tickets, digital screener access, and collect 100% transparent royalty splits.',
    badge: 'Direct Pay Escrow',
    color: '#ec4899',
  },
  {
    icon: <SensorsRoundedIcon sx={{ fontSize: 26, color: '#06b6d4' }} />,
    title: 'Real-Time Watch Parties',
    desc: 'Synchronized cinema rooms with real-time video chat, interactive host mic controls, and live viewer reactions.',
    badge: 'Sync Watch 60FPS',
    color: '#06b6d4',
  },
];

export default function FilmStreamingService() {
  const { isDarkMode } = useTheme();
  const [activeFilmIndex, setActiveFilmIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);

  const activeFilm = CINEMA_SHOWCASE[activeFilmIndex];

  return (
    <Box
      sx={{
        pt: { xs: 11, md: 15 },
        pb: 12,
        background: isDarkMode
          ? 'radial-gradient(circle at 80% 20%, rgba(7, 164, 245, 0.08) 0%, #030712 60%)'
          : 'radial-gradient(circle at 80% 20%, rgba(186, 230, 253, 0.4) 0%, #f8fafc 60%)',
        minHeight: '100vh',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Ambient background light orbs */}
      <Box
        sx={{
          position: 'absolute',
          top: '15%',
          right: '5%',
          width: 500,
          height: 500,
          background: 'radial-gradient(circle, rgba(7, 164, 245, 0.15) 0%, transparent 70%)',
          filter: 'blur(70px)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '20%',
          left: '5%',
          width: 450,
          height: 450,
          background: 'radial-gradient(circle, rgba(168, 85, 247, 0.12) 0%, transparent 70%)',
          filter: 'blur(70px)',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, mb: { xs: 8, md: 10 } }}>
        {/* Main Cinema Theater Stage Showcase Card */}
        <Box
          sx={{
            background: isDarkMode
              ? 'linear-gradient(145deg, rgba(15, 23, 42, 0.9) 0%, rgba(10, 15, 28, 0.95) 100%)'
              : 'linear-gradient(145deg, #ffffff 0%, #f0f7ff 100%)',
            borderRadius: { xs: '24px', md: '36px' },
            border: isDarkMode
              ? '1.5px solid rgba(7, 164, 245, 0.25)'
              : '1.5px solid rgba(2, 132, 199, 0.18)',
            boxShadow: isDarkMode
              ? '0 30px 80px rgba(0, 0, 0, 0.7), 0 0 50px rgba(7, 164, 245, 0.1)'
              : '0 24px 70px rgba(15, 23, 42, 0.08), 0 0 35px rgba(2, 132, 199, 0.06)',
            p: { xs: 3, sm: 4, md: 6 },
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Grid container spacing={4} alignItems="center">
            {/* Left Column: Hero Typography, Badges & Interactive Controls */}
            <Grid size={{ xs: 12, lg: 6 }}>
              <Box sx={{ pr: { lg: 3 } }}>
                {/* Top Badge: Filmhook Suite */}
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Chip
                    icon={<AutoAwesomeRoundedIcon sx={{ fontSize: '14px !important', color: '#0095F6 !important' }} />}
                    label="Filmhook Film Streaming Suite"
                    size="small"
                    sx={{
                      bgcolor: isDarkMode ? 'rgba(7, 164, 245, 0.14)' : 'rgba(2, 132, 199, 0.1)',
                      color: isDarkMode ? '#38bdf8' : '#0284c7',
                      fontWeight: 800,
                      fontSize: '0.74rem',
                      border: '1px solid rgba(7, 164, 245, 0.3)',
                      px: 0.8,
                    }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.6,
                      px: 1.2,
                      py: 0.35,
                      borderRadius: '12px',
                      bgcolor: 'rgba(239, 68, 68, 0.12)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                    }}
                  >
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        bgcolor: '#ef4444',
                        animation: 'pulse 1.3s infinite',
                      }}
                    />
                    <Typography sx={{ color: '#ef4444', fontSize: '0.68rem', fontWeight: 800, fontFamily: 'monospace' }}>
                      LIVE CINEMA CDN
                    </Typography>
                  </Box>
                </Box>

                {/* Main Heading */}
                <Typography
                  variant="h1"
                  sx={{
                    fontWeight: 900,
                    fontSize: { xs: '2.5rem', sm: '3.2rem', md: '3.8rem' },
                    color: isDarkMode ? '#ffffff' : '#0a192f',
                    letterSpacing: '-0.025em',
                    lineHeight: 1.1,
                    mb: 2,
                  }}
                >
                  Film Streaming
                </Typography>

                {/* Subtitle with Gradient Accent */}
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '1.25rem', sm: '1.55rem' },
                    color: isDarkMode ? '#38bdf8' : '#0284c7',
                    letterSpacing: '-0.01em',
                    mb: 2.2,
                  }}
                >
                  Watch and enjoy a wide range of movies
                </Typography>

                {/* Body Paragraph */}
                <Typography
                  sx={{
                    color: isDarkMode ? '#94a3b8' : '#475569',
                    fontSize: { xs: '0.98rem', md: '1.08rem' },
                    lineHeight: 1.7,
                    maxWidth: '560px',
                    mb: 4,
                  }}
                >
                  Watch and enjoy a wide range of movies, web series, and exclusive shows in stunning quality. Access premium content from independent creators and major studios globally with zero buffering.
                </Typography>

                {/* CTA Buttons Row */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 4.5 }}>
                  <Button
                    variant="contained"
                    size="large"
                    component="a"
                    href="https://play.google.com/store/apps/details?id=com.filmhookapps"
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<PlayArrowRoundedIcon sx={{ fontSize: '1.4rem !important' }} />}
                    sx={{
                      background: 'linear-gradient(135deg, #07a4f5 0%, #0284c7 100%)',
                      color: '#ffffff',
                      borderRadius: '30px',
                      px: 3.8,
                      py: 1.5,
                      fontWeight: 800,
                      textTransform: 'none',
                      fontSize: '0.96rem',
                      letterSpacing: '0.02em',
                      boxShadow: '0 8px 24px rgba(7, 164, 245, 0.45)',
                      transition: 'all 0.25s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 12px 30px rgba(7, 164, 245, 0.65)',
                        filter: 'brightness(1.1)',
                      },
                    }}
                  >
                    Get Started with Film Streaming
                  </Button>

                  <Button
                    variant="outlined"
                    size="large"
                    component={Link}
                    to="/products"
                    endIcon={<ArrowForwardRoundedIcon />}
                    sx={{
                      borderRadius: '30px',
                      px: 3,
                      py: 1.5,
                      fontWeight: 700,
                      textTransform: 'none',
                      fontSize: '0.94rem',
                      borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.18)' : 'rgba(2, 132, 199, 0.3)',
                      color: isDarkMode ? '#ffffff' : '#0284c7',
                      '&:hover': {
                        borderColor: '#0095F6',
                        bgcolor: isDarkMode ? 'rgba(7, 164, 245, 0.1)' : 'rgba(2, 132, 199, 0.05)',
                      },
                    }}
                  >
                    Browse Catalog
                  </Button>
                </Box>

                {/* Live Cinema Telemetry Strip (Atmos, 4K, 60FPS) */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.6,
                    borderRadius: '16px',
                    bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : '#ffffff',
                    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
                    maxWidth: '520px',
                    flexWrap: 'wrap',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <Box
                      component={motion.div}
                      animate={{ scale: [1, 1.25, 1] }}
                      transition={{ duration: 1.8, repeat: Infinity }}
                      sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10b981', boxShadow: '0 0 10px #10b981' }}
                    />
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: isDarkMode ? '#fff' : '#0f172a' }}>
                      Live Feature: {activeFilm.title}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.72rem', color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                    • {activeFilm.quality} • {activeFilm.audio}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Right Column: Live Animated Cinema Theater Stage & Multi-Poster Reel */}
            <Grid size={{ xs: 12, lg: 6 }}>
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2.5,
                }}
              >
                {/* Main Hero Screen Screen with Laser Scanner Beam & Play Overlay */}
                <Box
                  component={motion.div}
                  whileHover={{ scale: 1.01 }}
                  transition={{ duration: 0.3 }}
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: { xs: 260, sm: 340, md: 390 },
                    borderRadius: '24px',
                    overflow: 'hidden',
                    bgcolor: '#0a0f1d',
                    boxShadow: '0 25px 60px rgba(0, 0, 0, 0.5), 0 0 35px rgba(7, 164, 245, 0.2)',
                    border: isDarkMode ? '2px solid rgba(7, 164, 245, 0.4)' : '2px solid rgba(2, 132, 199, 0.3)',
                  }}
                >
                  {/* Hero Cinema Backdrop with Live Cross-Fade */}
                  <AnimatePresence mode="wait">
                    <Box
                      key={activeFilm.id}
                      component={motion.img}
                      src={activeFilm.poster}
                      alt={activeFilm.title}
                      initial={{ opacity: 0, scale: 1.06 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.96 }}
                      transition={{ duration: 0.55 }}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </AnimatePresence>

                  {/* Laser Scan Horizon Beam Animation */}
                  <Box
                    component={motion.div}
                    animate={{ y: [-150, 200, -150] }}
                    transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                    sx={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      height: '2px',
                      background: 'linear-gradient(90deg, transparent, #0095F6, #38bdf8, transparent)',
                      boxShadow: '0 0 15px #0095F6, 0 0 30px #38bdf8',
                      zIndex: 3,
                      pointerEvents: 'none',
                    }}
                  />

                  {/* Cinema Vignette Scrim */}
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.1) 40%, rgba(3, 7, 18, 0.95) 100%)',
                      zIndex: 2,
                    }}
                  />

                  {/* Top Header Floating Overlay: Live 4K Tag & Audio HUD */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 16,
                      left: 16,
                      right: 16,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      zIndex: 4,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={activeFilm.badge}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(239, 68, 68, 0.92)',
                          color: '#ffffff',
                          fontWeight: 900,
                          fontSize: '0.66rem',
                          height: 22,
                          letterSpacing: '0.06em',
                          boxShadow: '0 2px 10px rgba(239, 68, 68, 0.5)',
                        }}
                      />
                      <Chip
                        label={activeFilm.quality}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(15, 23, 42, 0.75)',
                          backdropFilter: 'blur(8px)',
                          color: '#38bdf8',
                          fontWeight: 800,
                          fontSize: '0.64rem',
                          height: 22,
                          border: '1px solid rgba(56, 189, 248, 0.3)',
                        }}
                      />
                    </Box>

                    <Chip
                      icon={<GradeRoundedIcon sx={{ fontSize: '13px !important', color: '#f59e0b !important' }} />}
                      label={`${activeFilm.rating} IMDb`}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(15, 23, 42, 0.8)',
                        backdropFilter: 'blur(8px)',
                        color: '#f59e0b',
                        fontWeight: 900,
                        fontSize: '0.68rem',
                        height: 22,
                        border: '1px solid rgba(245, 158, 11, 0.35)',
                      }}
                    />
                  </Box>

                  {/* Center Play Button Overlay */}
                  <Box
                    onClick={() => setIsPlaying(!isPlaying)}
                    component={motion.div}
                    whileHover={{ scale: 1.15 }}
                    whileTap={{ scale: 0.92 }}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      bgcolor: 'rgba(7, 164, 245, 0.95)',
                      boxShadow: '0 0 35px rgba(7, 164, 245, 0.8), 0 0 60px rgba(7, 164, 245, 0.4)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      zIndex: 4,
                      color: '#ffffff',
                    }}
                  >
                    {isPlaying ? (
                      <PauseRoundedIcon sx={{ fontSize: '2rem' }} />
                    ) : (
                      <PlayArrowRoundedIcon sx={{ fontSize: '2.4rem', ml: 0.3 }} />
                    )}
                  </Box>

                  {/* Bottom Information Bar on Hero Screen */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 16,
                      left: 16,
                      right: 16,
                      zIndex: 4,
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box>
                      <Typography sx={{ color: '#38bdf8', fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        {activeFilm.category}
                      </Typography>
                      <Typography sx={{ color: '#ffffff', fontSize: { xs: '1.2rem', sm: '1.45rem' }, fontWeight: 900 }}>
                        {activeFilm.title}
                      </Typography>
                    </Box>

                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.68rem', fontWeight: 700 }}>
                        STREAM AUDIENCE
                      </Typography>
                      <Typography sx={{ color: '#ffffff', fontSize: '0.95rem', fontWeight: 900, fontFamily: 'monospace' }}>
                        {activeFilm.views} Viewers
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Interactive Multi-Poster Reel Dock (Mad Max, Spectre, Matrix, Scarface) */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      color: isDarkMode ? '#94a3b8' : '#64748b',
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      mb: 1.2,
                    }}
                  >
                    SELECT CINEMA STREAM TO PREVIEW:
                  </Typography>

                  <Grid container spacing={1.5}>
                    {CINEMA_SHOWCASE.map((film, index) => {
                      const isSelected = activeFilmIndex === index;
                      return (
                        <Grid size={{ xs: 6, sm: 3 }} key={film.id}>
                          <Box
                            component={motion.div}
                            whileHover={{ y: -5, scale: 1.02 }}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => setActiveFilmIndex(index)}
                            sx={{
                              height: 100,
                              borderRadius: '16px',
                              overflow: 'hidden',
                              position: 'relative',
                              cursor: 'pointer',
                              border: isSelected
                                ? `2.5px solid ${film.accent}`
                                : isDarkMode
                                ? '1.5px solid rgba(255, 255, 255, 0.1)'
                                : '1.5px solid rgba(0, 0, 0, 0.08)',
                              boxShadow: isSelected
                                ? `0 10px 25px ${film.accent}50`
                                : '0 4px 12px rgba(0, 0, 0, 0.06)',
                              transition: 'all 0.25s ease',
                            }}
                          >
                            <Box
                              component="img"
                              src={film.poster}
                              alt={film.title}
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                filter: isSelected ? 'brightness(1.05)' : 'brightness(0.75)',
                                transition: 'filter 0.3s ease',
                              }}
                            />

                            {/* Scrim */}
                            <Box
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                background: 'linear-gradient(180deg, transparent 30%, rgba(0,0,0,0.85) 100%)',
                              }}
                            />

                            {/* Bottom Title Label */}
                            <Box sx={{ position: 'absolute', bottom: 6, left: 8, right: 8 }}>
                              <Typography sx={{ color: '#ffffff', fontSize: '0.7rem', fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {film.title}
                              </Typography>
                              <Typography sx={{ color: film.accent, fontSize: '0.58rem', fontWeight: 700 }}>
                                {film.quality}
                              </Typography>
                            </Box>

                            {/* Active Playing Beacon */}
                            {isSelected && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 6,
                                  right: 6,
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: film.accent,
                                  boxShadow: `0 0 10px ${film.accent}`,
                                }}
                              />
                            )}
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* High-Performance Cinema Infrastructure Features Grid */}
      <Container maxWidth="xl">
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: '#07a4f5',
                boxShadow: '0 0 10px #07a4f5',
                animation: 'pulse 1.4s infinite',
              }}
            />
            <Typography
              sx={{
                fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                fontSize: '0.74rem',
                fontWeight: 800,
                letterSpacing: '0.1em',
                color: isDarkMode ? '#94a3b8' : '#64748b',
                textTransform: 'uppercase',
              }}
            >
              HIGH-PERFORMANCE CINEMA INFRASTRUCTURE
            </Typography>
          </Box>
          <Typography
            variant="h3"
            fontWeight={900}
            sx={{
              color: isDarkMode ? '#fff' : '#0f172a',
              fontSize: { xs: '2rem', md: '2.8rem' },
              letterSpacing: '-0.02em',
            }}
          >
            Everything you need for{' '}
            <Box component="span" sx={{ color: '#07a4f5' }}>
              Film Streaming
            </Box>
          </Typography>
        </Box>

        {/* 6 High-Performance Infrastructure Cards */}
        <Grid container spacing={3}>
          {STREAMING_CAPABILITIES.map((cap, idx) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={idx}>
              <Box
                component={motion.div}
                whileHover={{ y: -7, scale: 1.02 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                sx={{
                  background: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : '#ffffff',
                  p: { xs: 3, sm: 3.5, md: 4 },
                  borderRadius: '24px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  border: isDarkMode
                    ? '1px solid rgba(255, 255, 255, 0.08)'
                    : '1px solid rgba(0, 0, 0, 0.06)',
                  boxShadow: isDarkMode
                    ? '0 8px 30px rgba(0,0,0,0.4)'
                    : '0 8px 25px rgba(15,23,42,0.05)',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'default',
                  transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '18%',
                    right: '18%',
                    height: '3.5px',
                    background: cap.color,
                    borderRadius: '0 0 4px 4px',
                    opacity: 0.8,
                    transition: 'all 0.35s ease',
                  },
                  '&:hover': {
                    borderColor: cap.color,
                    boxShadow: `0 18px 45px ${cap.color}25`,
                    background: isDarkMode ? 'rgba(255, 255, 255, 0.055)' : '#ffffff',
                    '&::before': {
                      left: '5%',
                      right: '5%',
                      height: '4px',
                      boxShadow: `0 0 14px ${cap.color}`,
                      opacity: 1,
                    },
                  },
                }}
              >
                <Box>
                  {/* Top: Icon & Badge */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.2 }}>
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: '16px',
                        background: `${cap.color}18`,
                        border: `1.5px solid ${cap.color}35`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 6px 18px ${cap.color}25`,
                      }}
                    >
                      {cap.icon}
                    </Box>

                    <Chip
                      label={cap.badge}
                      size="small"
                      sx={{
                        bgcolor: `${cap.color}15`,
                        color: cap.color,
                        fontWeight: 800,
                        fontSize: '0.68rem',
                        border: `1px solid ${cap.color}35`,
                      }}
                    />
                  </Box>

                  <Typography
                    variant="h5"
                    fontWeight={800}
                    sx={{ color: isDarkMode ? '#fff' : '#0f172a', mb: 1, fontSize: '1.22rem' }}
                  >
                    {cap.title}
                  </Typography>

                  <Typography sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '0.86rem', lineHeight: 1.6 }}>
                    {cap.desc}
                  </Typography>
                </Box>

                <Box sx={{ mt: 3, pt: 2, borderTop: isDarkMode ? '1px solid rgba(255, 255, 255, 0.06)' : '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleRoundedIcon sx={{ fontSize: 16, color: '#10b981' }} />
                  <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: isDarkMode ? '#cbd5e1' : '#334155' }}>
                    Production-grade SLA active
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
